import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LinkAuthenticationElement,
  AddressElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent, StripeLinkAuthenticationElementChangeEvent } from '@stripe/stripe-js';
import ActionContainer from '../ActionContainer';
import Button from '../Button';
import ErrorMessage, { useError } from '../ErrorMessage';
import { logInternalError } from '../../lib/errors';
import { useLocale } from '../../contexts/LocaleContext';
import ShippingOptionsList from '../../views/Checkout/components/ShippingOptionsList';
import * as types from '../../types';
import MarketingOptInCheckbox from '../MarketingOptInCheckbox';
import styles from './StripePaymentForm.module.scss';

export type StripeAddressValue = StripeAddressElementChangeEvent['value'];

export type StripePaymentMethodDetails =
  | { type: 'card'; brand: string; last4: string; expMonth: number; expYear: number }
  | { type: 'us_bank_account'; bankName: string; last4: string; accountType: string };

/** @deprecated use StripePaymentMethodDetails instead */
export type StripeCardDetails = StripePaymentMethodDetails;

interface StripePaymentFormProps {
  /** called with the stripe confirmation token id and payment method details after successful form submission */
  onConfirmationToken: (tokenId: string, paymentMethodDetails: StripePaymentMethodDetails) => void;
  /** called when the address element value changes */
  onAddressChange?: (address: StripeAddressValue) => void;
  /** called when the link authentication element email changes */
  onEmailChange?: (email: string) => void;
  /** called when shipping speed selection changes */
  onShippingCodeChange?: (shippingCode: types.ShippingCode) => void;
  /** whether any cart item is a preorder (affects shipping options) */
  anyPreorder: boolean;
  /** whether the shipping address supports expedited shipping */
  canExpedite: boolean;
  /** currently selected shipping code */
  selectedShippingCode: types.ShippingCode | undefined;
  /** external submitting state (e.g. parent is saving billing info) */
  submitting?: boolean;
  /** external error message to display */
  serverError?: string;
}

export default function StripePaymentForm({
  onConfirmationToken,
  onAddressChange,
  onEmailChange,
  onShippingCodeChange,
  anyPreorder,
  canExpedite,
  selectedShippingCode,
  submitting: externalSubmitting = false,
  serverError,
}: StripePaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { country } = useLocale();

  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const { error: internalError, errorID, setError, clearError } = useError();

  const [stripeReady, setStripeReady] = useState(false);

  // Track auto-fill state to detect Link logout and force remount with correct country defaults.
  // Stripe's defaultValues only apply on mount, so when Link clears auto-filled fields,
  // the country resets to US instead of the fi_country cookie value.
  const [elementKey, setElementKey] = useState(0);
  const [wasAutoFilled, setWasAutoFilled] = useState(false);

  const isSubmitting = internalSubmitting || externalSubmitting;
  const displayError = serverError || internalError;

  const handleAddressChange = useCallback(
    (event: StripeAddressElementChangeEvent) => {
      const isEmpty = !event.value.address.line1 && !event.value.name;
      if (event.complete) {
        setWasAutoFilled(true);
      } else if (wasAutoFilled && isEmpty) {
        setWasAutoFilled(false);
        setElementKey((k) => k + 1);
      }
      onAddressChange?.(event.value);
    },
    [wasAutoFilled, onAddressChange],
  );

  const handleEmailChange = useCallback(
    (event: StripeLinkAuthenticationElementChangeEvent) => {
      onEmailChange?.(event.value.email);
    },
    [onEmailChange],
  );

  const submitForm = useCallback(async () => {
    if (!stripe || !elements || isSubmitting) {
      return;
    }

    if (!selectedShippingCode) {
      setError(t('please_select_a_shipping_method'));
      return;
    }

    clearError();
    setInternalSubmitting(true);

    try {
      // validate all elements before creating the confirmation token
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? t('something_went_wrong'));
        return;
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params: {
          return_url: `${window.location.origin}/checkout/complete`,
        },
      });

      if (tokenError) {
        setError(tokenError.message ?? t('something_went_wrong'));
        return;
      }

      const preview = confirmationToken.payment_method_preview;
      const paymentMethodDetails: StripePaymentMethodDetails =
        preview?.type === 'us_bank_account'
          ? {
              type: 'us_bank_account',
              bankName: preview.us_bank_account?.bank_name ?? '',
              last4: preview.us_bank_account?.last4 ?? '',
              accountType: preview.us_bank_account?.account_type ?? '',
            }
          : {
              type: 'card',
              brand: preview?.card?.brand ?? '',
              last4: preview?.card?.last4 ?? '',
              expMonth: preview?.card?.exp_month ?? 0,
              expYear: preview?.card?.exp_year ?? 0,
            };
      onConfirmationToken(confirmationToken.id, paymentMethodDetails);
    } catch (err) {
      logInternalError(err, { tags: { component: 'StripePaymentForm' } });
      setError(t('something_went_wrong'));
    } finally {
      setInternalSubmitting(false);
    }
  }, [stripe, elements, isSubmitting, selectedShippingCode, clearError, setError, onConfirmationToken, t]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      void submitForm();
    },
    [submitForm],
  );

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formContent}>
        <div className={styles.section}>
          <h4>{t('email')}</h4>
          <LinkAuthenticationElement onChange={handleEmailChange} />
        </div>

        <div className={styles.section}>
          <h4>{t('shipping')}</h4>
          <AddressElement
            key={`address-${elementKey}`}
            options={{
              mode: 'shipping',
              fields: { phone: 'always' },
              validation: { phone: { required: 'always' } },
              allowedCountries: [country],
              defaultValues: { address: { country } },
            }}
            onChange={handleAddressChange}
          />
        </div>

        {stripeReady && (
          <div className={styles.section}>
            <ShippingOptionsList
              anyPreorder={anyPreorder}
              canExpedite={canExpedite}
              currentlySelected={selectedShippingCode}
              onSelect={(code) => onShippingCodeChange?.(code)}
            />
          </div>
        )}

        <div className={styles.section}>
          <h4>{t('payment')}</h4>
          <PaymentElement
            key={`payment-${elementKey}`}
            onReady={() => setStripeReady(true)}
            options={{
              defaultValues: {
                billingDetails: { address: { country } },
              },
            }}
          />
        </div>

        <MarketingOptInCheckbox />

        {displayError && (
          <ErrorMessage
            errors={[displayError]}
            errorID={errorID}
            trackError={(message) => logInternalError(new Error(message), { tags: { component: 'StripePaymentForm' } })}
          />
        )}
      </div>

      <div className={styles.actionContainer}>
        <ActionContainer>
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            {t('continue')}
          </Button>
        </ActionContainer>
      </div>
    </form>
  );
}
